// Timezone-aware formatting helpers.
// Backend stores every timestamp as UTC ISO. When an admin views country-
// scoped data, we render those UTC instants in the country's LOCAL time so
// numbers make sense to a supervisor in that country's field office —
// regardless of where the admin's laptop happens to sit.
//
// When no country is chosen, we fall back to the browser's local time (the
// admin's own timezone). This matches the user's requirement:
//   "if no country is selected, show the India time (the admin's), but if
//    Niger is selected show Niger local time."

/** Get the IANA timezone name for a country id, or null if unknown. */
export function tzForCountry(countries, countryId) {
  if (!countryId || !countries) return null;
  const c = countries.find((x) => x.id === countryId);
  return c?.timezone || null;
}

/** Format a UTC ISO string as HH:mm in a specific IANA zone (or browser). */
export function formatTime(iso, tz) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: tz || undefined,
    }).format(d);
  } catch {
    return "—";
  }
}

/** Format a UTC ISO string as HH:mm:ss in a specific IANA zone (or browser). */
export function formatTimeSec(iso, tz) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: tz || undefined,
    }).format(d);
  } catch {
    return "—";
  }
}

/** Format as "d MMM yyyy · HH:mm" in a specific IANA zone. */
export function formatDateTime(iso, tz) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const date = new Intl.DateTimeFormat("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: tz || undefined,
    }).format(d);
    const time = formatTime(iso, tz);
    return `${date} · ${time}`;
  } catch {
    return "—";
  }
}

/**
 * Human tag like "IST" or "WAT" for the given IANA zone.
 * Falls back to the raw zone name (e.g. "Asia/Kolkata") if the browser can't
 * derive a short name.
 */
export function tzShort(tz) {
  if (!tz) return "local";
  try {
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(new Date());
    const p = parts.find((x) => x.type === "timeZoneName");
    return p?.value || tz;
  } catch {
    return tz;
  }
}
